import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { readFilterCatalog } from '@api/survey_dashboard';

import {
  buildGlobalFilterFacetsFromCatalog,
  buildGlobalFilterSelection,
  buildReportingState,
  updateGlobalFilterSearchParams,
} from '../components/Responses/utilities/helpers';

const EMPTY_SELECTION = { compareBy: null, logic: 'AND', rows: [] };

const buildReportingQueryKey = (selection, facets) =>
  JSON.stringify(
    Object.entries(buildReportingState(selection, facets).query).sort(([left], [right]) =>
      left.localeCompare(right)
    )
  );

/** Loads the redesign catalog, hydrates its initial URL state, and writes user changes back. */
const useGlobalFiltersUrlState = ({ enabled, publicToken, surveyId, surveyQuestions }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState({ error: null, facets: null, selection: EMPTY_SELECTION });

  useEffect(() => {
    if (!enabled || surveyId == null) {
      setState({ error: null, facets: null, selection: EMPTY_SELECTION });
      return undefined;
    }

    const controller = new AbortController();
    setState({ error: null, facets: null, selection: EMPTY_SELECTION });

    readFilterCatalog({ publicToken, signal: controller.signal, surveyId })
      .then((catalog) => {
        if (controller.signal.aborted) return;
        const facets = buildGlobalFilterFacetsFromCatalog(catalog, surveyQuestions);
        setState({
          error: null,
          facets,
          selection: buildGlobalFilterSelection(searchParams, facets),
        });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.warn('[Dashboard] Failed to fetch filter catalog:', error);
        setState({ error, facets: null, selection: EMPTY_SELECTION });
      });

    return () => controller.abort();
  }, [enabled, publicToken, surveyId, surveyQuestions]);

  const globalFilterFacets = state.facets || [];
  const searchParamsKey = searchParams.toString();
  const urlSelection = useMemo(
    () => (state.facets ? buildGlobalFilterSelection(searchParams, state.facets) : EMPTY_SELECTION),
    [searchParamsKey, state.facets]
  );
  const urlReportingQueryKey = buildReportingQueryKey(urlSelection, globalFilterFacets);
  const globalFiltersRedesignState = useMemo(
    () => buildReportingState(state.selection, globalFilterFacets),
    [globalFilterFacets, state.selection]
  );

  useEffect(() => {
    if (!state.facets) return;

    setState((current) => {
      if (buildReportingQueryKey(current.selection, state.facets) === urlReportingQueryKey) {
        return current;
      }

      const urlFacetIds = new Set(urlSelection.rows.map(({ facetId }) => facetId));
      const emptyRows = current.selection.rows.filter(
        ({ facetId, values }) => values.length === 0 && !urlFacetIds.has(facetId)
      );

      return {
        ...current,
        selection: { ...urlSelection, rows: [...urlSelection.rows, ...emptyRows] },
      };
    });
  }, [state.facets, urlReportingQueryKey]);

  const onGlobalFilterSelectionChange = useCallback(
    (selection) => {
      if (!state.facets) return;

      setState((current) => ({ ...current, selection }));
      const nextSearchParams = updateGlobalFilterSearchParams(
        searchParams,
        selection,
        state.facets
      );
      if (nextSearchParams.toString() !== searchParams.toString()) {
        setSearchParams(nextSearchParams, { replace: true });
      }
    },
    [searchParams, setSearchParams, state.facets]
  );

  return {
    globalFilterFacets,
    globalFilterSelection: state.selection,
    globalFiltersRedesignError: state.error,
    globalFiltersRedesignState,
    isGlobalFiltersRedesignLoading: enabled && !state.error && state.facets == null,
    isGlobalFiltersRedesignReady: enabled && !state.error && state.facets != null,
    onGlobalFilterSelectionChange,
  };
};

export default useGlobalFiltersUrlState;
